import { useState } from "react";
import { INDUSTRIES } from "@/constants/landing";
import { AlertTriangle, Zap, Sparkles } from "lucide-react";

export default function Industries() {
  const [active, setActive] = useState(0);
  const ind = INDUSTRIES[active];

  return (
    <section id="industries" className="py-20 sm:py-28 relative" data-testid="industries-section">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
        <div className="max-w-[640px] mb-8 sm:mb-10">
          <span className="yg-eyebrow">// Built for verticals</span>
          <h2 className="yg-h2 text-[clamp(30px,4.6vw,52px)] mt-3 text-white">
            Every industry.
            <br />
            <span className="text-[var(--yg-violet-light)]">Every friction. Fixed.</span>
          </h2>
        </div>

        {/* Tab strip — horizontal scroll on mobile */}
        <div className="relative -mx-5 sm:-mx-8 mb-5 sm:mb-6">
          <div className="flex items-center gap-2 px-5 sm:px-8 overflow-x-auto no-scrollbar snap-x snap-mandatory" role="tablist" data-testid="industry-tabs">
            {INDUSTRIES.map((it, i) => {
              const on = i === active;
              return (
                <button
                  key={it.key}
                  role="tab"
                  aria-selected={on}
                  onClick={() => setActive(i)}
                  className={`shrink-0 snap-start flex items-center gap-2 px-3.5 py-2 rounded-full text-[12px] font-semibold transition-all border ${
                    on
                      ? "bg-[var(--yg-violet)] text-white border-transparent shadow-[0_8px_20px_-8px_var(--yg-violet-glow)]"
                      : "bg-white/[0.03] text-[var(--yg-text-mute)] border-[var(--yg-border)] hover:text-white hover:border-[var(--yg-border-strong)]"
                  }`}
                  data-testid={`industry-tab-${it.key}`}
                >
                  <span className="text-[14px] leading-none">{it.emoji}</span>
                  <span className="whitespace-nowrap">{it.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Panel */}
        <div
          key={ind.key}
          className="yg-card p-5 sm:p-7 lg:p-8 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)_minmax(0,1fr)] gap-5 lg:gap-7"
          data-testid={`industry-panel-${ind.key}`}
          style={{ animation: "yg-rise 500ms both cubic-bezier(.2,.9,.3,1)" }}
        >
          {/* Header + Pain (col 1) */}
          <div className="lg:border-r lg:border-[var(--yg-border)] lg:pr-7 min-w-0">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[32px] leading-none">{ind.emoji}</span>
              <div>
                <div className="yg-mono text-[9.5px] tracking-[0.24em] uppercase text-[var(--yg-text-dim)]">Industry</div>
                <div className="text-white font-extrabold text-[18px] tracking-[-0.015em] leading-tight">{ind.name}</div>
              </div>
            </div>

            <ColHead icon={AlertTriangle} label="Pain points" color="#EF4444" />
            <ul className="mt-3 space-y-2.5">
              {ind.pain.map((p, i) => (
                <li key={p} className="flex gap-2.5 text-[13px] text-white/90 leading-snug" style={{ animation: `yg-rise 500ms ${100 + i*80}ms both` }}>
                  <span className="text-[#EF4444] shrink-0">•</span> {p}
                </li>
              ))}
            </ul>
          </div>

          {/* Friction (col 2) */}
          <div className="lg:border-r lg:border-[var(--yg-border)] lg:pr-7 min-w-0">
            <ColHead icon={Zap} label="Friction moments" color="#F59E0B" />
            <ul className="mt-3 space-y-2.5">
              {ind.friction.map((p, i) => (
                <li key={p} className="flex gap-2.5 text-[13px] text-white/90 leading-snug" style={{ animation: `yg-rise 500ms ${200 + i*80}ms both` }}>
                  <span className="text-[#F59E0B] shrink-0">→</span> {p}
                </li>
              ))}
            </ul>
          </div>

          {/* Solution (col 3) */}
          <div className="min-w-0">
            <ColHead icon={Sparkles} label="Yugiq solves it" color="var(--yg-violet-light)" />
            <ul className="mt-3 space-y-2.5">
              {ind.solution.map((p, i) => (
                <li key={p} className="flex gap-2.5 text-[13px] text-white leading-snug font-medium" style={{ animation: `yg-rise 500ms ${300 + i*80}ms both` }}>
                  <span className="text-[var(--yg-violet-light)] shrink-0">✓</span> {p}
                </li>
              ))}
            </ul>

            <div className="mt-5 pt-5 border-t border-[var(--yg-border)] grid grid-cols-3 gap-2">
              {ind.results.map((r) => (
                <div key={r.l} className="rounded-[10px] border border-[var(--yg-border)] bg-black/25 p-2.5 text-center">
                  <div className="text-white font-extrabold text-[16px] tracking-tight">{r.v}</div>
                  <div className="yg-mono text-[9px] tracking-[0.16em] text-[var(--yg-text-dim)] uppercase mt-0.5">{r.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ColHead({ icon: Icon, label, color }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-6 h-6 rounded-[7px] flex items-center justify-center" style={{ background: `${color}18`, border: `1px solid ${color}44` }}>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </span>
      <span className="yg-mono text-[10px] tracking-[0.22em] uppercase" style={{ color: "var(--yg-text-mute)" }}>{label}</span>
    </div>
  );
}
